import { FormProvider, useForm } from 'react-hook-form';
import {
  assocPath,
  compose,
  dissoc,
  isNil,
  map,
  modify,
  modifyPath,
  path,
  pathSatisfies,
  prop,
  propOr,
  when
} from 'ramda';
import PropTypes from 'prop-types';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import useSouscriptionCustomerGranteesForm from './hooks/useSouscriptionCustomerGranteesForm.js';
import SouscriptionCustomerForm from './components/SouscriptionCustomerForm/SouscriptionCustomerForm.jsx';
import { Icon, Loading, PageTitle, Tabs } from '@fasstech/middle-design-system';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import GranteesForm from './components/GranteesForm/GranteesForm.jsx';
import useGetSouscriptionCustomerGranteesLegalIndicatorsQuery from './hooks/useGetSouscriptionCustomerGranteesLegalIndicatorsQuery.js';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import moment from 'moment';
import { FORMAT_DATE_FR } from '@@viewHelpers/date.js';
import hasExtraUnrequiredFields from './extra/hasExtraUnrequiredFields.js';
import { offerCodeEnum } from '@@viewEnums';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { formatExtraDefaultValues } from './extra/formatExtraData.js';

const SouscriptionCustomerGranteesForm = ({
  jobs,
  regimeObligatoire,
  professionalSituations,
  socioProfessionalCategories,
  activities,
  data: project,
  middleFeatures,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  familySituations,
  getCountries
}) => {
  const { context } = useProcess();
  const effectDate = prop('offerEffectDate', project);
  const {
    getSouscriptionCustomerGranteesLegalIndicators: legalIndicators,
    loading: isFetchingIndicators
  } = useGetSouscriptionCustomerGranteesLegalIndicatorsQuery({
    variables: {
      effectDate
    },
    skip: context.offerCode !== offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION
  });

  const pathDefaultTo = (path, defaultValue) =>
    when(pathSatisfies(isNilOrEmpty, path), assocPath(path, defaultValue));

  const formatDate = (date) => (isNil(date) ? null : moment(date, FORMAT_DATE_FR).toDate());

  const formatGrantees = modify(
    'grantees',
    map(
      compose(
        modifyPath(['attachedSocialSecurities', 0, 'birthDate'], formatDate),
        modify('birthDate', formatDate)
      )
    )
  );

  const formatCustomer = compose(
    modifyPath(['customer', 'birthDate'], formatDate),
    modifyPath(['customer', 'socialSecurities', 0, 'dateCSS'], formatDate)
  );

  const formatOfferEffectDate = modify('offerEffectDate', formatDate);

  const madelinMonths = propOr([], 'madelinMonths')(project);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: compose(
      formatGrantees,
      formatCustomer,
      formatOfferEffectDate,
      pathDefaultTo(['customer', 'madelinMonth'], path([0, 'value'], madelinMonths)),
      pathDefaultTo(['customer', 'isMadelin'], false),
      pathDefaultTo(['customer', 'sendMode'], 'DIGITAL'),
      pathDefaultTo(['customer', 'socialSecurities'], []),
      dissoc('madelinMonths'),
      formatExtraDefaultValues
    )(project)
  });

  const customer = form.watch('customer');
  const grantees = form.watch('grantees');

  const {
    tabs,
    setTabs,
    setSelectedGrantee,
    updateVisibility,
    isButtonDisabled,
    displayTabsPagination
  } = useTabs({
    grantees: [customer, ...grantees],
    getMiddleText
  });

  const {
    onSubmit,
    isUnauthorized,
    postalCodeFranceMetro,
    postalCodeDROM,
    onPrevious,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild,
    offerCode,
    isValid
  } = useSouscriptionCustomerGranteesForm({
    project,
    middleFeatures,
    form,
    customer,
    grantees
  });

  if (isFetchingIndicators) return <Loading />;

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout>
          <Icon
            name={tabs.selectedTab === 0 ? 'informations_adherent' : 'informations_beneficiaire'}
            size={70}
          />
          <PageTitle>
            {getMiddleText(
              tabs.selectedTab === 0
                ? 'ADHERENT_GENERAL_VIEW_TITLE'
                : 'BENEFICIAIRE_LABEL_GENERAL_VIEW_TITLE'
            )}
          </PageTitle>
          <div className={'flex flex-col gap-4'}>
            <TabsPagination
              updateVisibility={updateVisibility}
              isButtonDisabled={isButtonDisabled}
              show={displayTabsPagination}
            >
              <Tabs
                items={tabs.tabs}
                selected={tabs.selectedTab}
                onClick={(index) => {
                  setTabs({
                    selectedTab: index,
                    tabs: tabs.tabs
                  });
                  const grantee = grantees[index];
                  if (grantee) {
                    setSelectedGrantee(grantee);
                  }
                }}
                isDisplayed={tabs.tabs.length > 1}
              />
            </TabsPagination>
          </div>
          <SouscriptionCustomerForm
            getMiddleBoolean={getMiddleBoolean}
            getMiddleText={getMiddleText}
            getMiddleNumber={getMiddleNumber}
            professionalSituations={professionalSituations}
            socioProfessionalCategories={socioProfessionalCategories}
            activities={activities}
            regimeObligatoire={regimeObligatoire}
            jobs={jobs}
            selectedTab={tabs.selectedTab}
            legalIndicators={legalIndicators}
            familySituations={familySituations}
            getCountries={getCountries}
            madelinMonths={madelinMonths}
            project={project}
          />

          <GranteesForm
            professionalSituations={professionalSituations}
            regimeObligatoire={regimeObligatoire}
            getMiddleText={getMiddleText}
            getMiddleBoolean={getMiddleBoolean}
            getMiddleNumber={getMiddleNumber}
            postalCodeFranceMetro={postalCodeFranceMetro}
            postalCodeDROM={postalCodeDROM}
            selectedTab={tabs.selectedTab}
            fields={grantees}
            form={form}
            minBirthDateReducChild={minBirthDateReducChild}
            maxBirthDateReducChild={maxBirthDateReducChild}
            minBirthDateHospiChild={minBirthDateHospiChild}
            maxBirthDateHospiChild={maxBirthDateHospiChild}
            offerCode={offerCode}
            madelinMonths={madelinMonths}
            middleFeatures={middleFeatures}
          />
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={onPrevious}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          displayResumeButton
          nextButtonIsDisabled={isUnauthorized || !isValid || !hasExtraUnrequiredFields(form)}
          isFormError={!isValid || !hasExtraUnrequiredFields(form)}
          getMiddleText={getMiddleText}
        />
      </form>
    </FormProvider>
  );
};

SouscriptionCustomerGranteesForm.propTypes = {
  jobs: PropTypes.array,
  regimeObligatoire: PropTypes.object,
  professionalSituations: PropTypes.array,
  socioProfessionalCategories: PropTypes.array,
  activities: PropTypes.array,
  data: PropTypes.object,
  middleFeatures: PropTypes.object,
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func,
  familySituations: PropTypes.array,
  getCountries: PropTypes.object
};

export default SouscriptionCustomerGranteesForm;
